import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Debug: Log menu state changes
  useEffect(() => {
    console.log('Menu state changed to:', isMenuOpen);
  }, [isMenuOpen]);
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Account for fixed navbar height (approximately 60-70px on mobile)
      const navbarHeight = document.querySelector('.hero-navbar')?.offsetHeight || 70;
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 20; // Extra 20px padding
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false); // Close menu after navigation
  };

  // Track scroll position
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsScrolled(scrollPosition > 50); // Show logo in navbar after scrolling 50px
          
          // Close menu when scrolling (but keep navbar visible)
          if (scrollPosition !== lastScrollY) {
            setIsMenuOpen(false);
          }
          
          lastScrollY = scrollPosition;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      const menu = document.querySelector('.hero-mobile-menu');
      const menuButton = document.querySelector('.hero-menu-btn');
      
      // Don't close if clicking inside menu or menu button
      if (menu && menu.contains(target)) return;
      if (menuButton && menuButton.contains(target)) return;
      
      // Close menu if clicking outside
      setIsMenuOpen(false);
    };

    // Add listener after a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <section className="hero-wrapper">
      {/* Vertical Dividers */}
      <div className="hero-divider hero-divider-1"></div>
      <div className="hero-divider hero-divider-2"></div>
      <div className="hero-divider hero-divider-3"></div>
      <div className="hero-divider hero-divider-4"></div>
      <div className="hero-divider hero-divider-5"></div>

      {/* Logo - Separate on Mobile (hidden when scrolled) */}
      <div 
        className={`hero-logo-mobile ${isScrolled ? 'hero-logo-mobile-hidden' : ''}`}
        onClick={() => navigate('/')}
        style={{ cursor: 'pointer' }}
      >
        <img 
          src={process.env.PUBLIC_URL + '/images/catptivework-tops.svg'} 
          alt="CaptiveWorks"
          className="hero-logo-image"
        />
      </div>

      {/* Integrated Navbar */}
      <div className={`hero-navbar ${isScrolled ? 'hero-navbar-scrolled' : ''}`}>
        <div className="hero-navbar-left">
          {/* Desktop: Text links */}
          <button 
            className="hero-nav-link hero-nav-link-desktop" 
            onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}
          >
            How it Works
          </button>
          <button 
            className="hero-nav-link hero-nav-link-desktop" 
            onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
          >
            FAQ
          </button>
          {/* Mobile: Menu icon */}
          <button 
            className={`hero-menu-btn hero-menu-btn-mobile ${isMenuOpen ? 'menu-open' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Menu button clicked, current state:', isMenuOpen);
              const newState = !isMenuOpen;
              console.log('Setting menu state to:', newState);
              setIsMenuOpen(newState);
            }}
            aria-label="Menu"
            aria-expanded={isMenuOpen}
            type="button"
          >
            <img 
              src={process.env.PUBLIC_URL + '/images/menu.svg'} 
              alt="Menu"
              className="hero-menu-icon"
            />
          </button>
        </div>
        <div 
          className={`hero-navbar-logo ${isScrolled ? 'hero-navbar-logo-mobile-visible' : ''}`}
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        >
          <img 
            src={process.env.PUBLIC_URL + '/images/catptivework-tops.svg'} 
            alt="CaptiveWorks"
            className="hero-logo-image"
          />
        </div>
        <div className="hero-navbar-right">
          {/* Desktop: Schedule a Call button */}
          <button 
            className="btn-schedule-hero-nav btn-schedule-hero-nav-desktop"
            onClick={() => navigate('/schedule-call')}
          >
            <span>Schedule a Call</span>
          </button>
          {/* Mobile: Phone icon */}
          <button 
            className="btn-phone-hero-nav btn-phone-hero-nav-mobile"
            onClick={() => navigate('/schedule-call')}
            aria-label="Schedule a Call"
          >
            <img 
              src={process.env.PUBLIC_URL + '/images/phone.svg'} 
              alt="Schedule a Call"
              className="hero-phone-icon"
            />
          </button>
        </div>
        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div 
            className="hero-mobile-menu" 
            style={{ 
              display: 'flex',
              visibility: 'visible',
              opacity: 1
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="hero-mobile-menu-item"
              type="button"
              onClick={(e) => { 
                e.preventDefault();
                e.stopPropagation();
                console.log('How it Works clicked');
                scrollToSection('how-it-works'); 
              }}
            >
              How it Works
            </button>
            <button 
              className="hero-mobile-menu-item"
              type="button"
              onClick={(e) => { 
                e.preventDefault();
                e.stopPropagation();
                console.log('FAQ clicked');
                scrollToSection('faq'); 
              }}
            >
              FAQ
            </button>
          </div>
        )}
      </div>

      {/* Hero Content */}
      <div className="hero-content-new">
        <h1 className="hero-title-new">
          Build your Captive <br/> Control Your Risk
        </h1>
        <p className="hero-subtitle-new">
          CaptiveWorks helps property managers and owners set up and manage their own insurance captives — turning compliance into a profit center.
        </p>
      </div>

      {/* Corner Indicators */}
      <div className="hero-corner hero-corner-1"></div>
      <div className="hero-corner hero-corner-2"></div>
    </section>
  );
};

export default Hero;
